import { List } from 'react-window';
import Box from '@mui/material/Box';
import { bool, func, number, object } from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import ContentLoader from 'react-content-loader';
import { InfiniteLoader } from 'react-window-infinite-loader';
import EmployeeListItem from './EmployeeListItem';

const ROW_HEIGHT = 80;

const Row = ({ ariaAttributes, index, style, employeesRef }) => {
  const employee = employeesRef.current[index];
  return employee ? (
    <EmployeeListItem {...ariaAttributes} employee={employee} style={style} />
  ) : (
    <Box {...ariaAttributes} sx={{ flexGrow: 1 }} style={style}>
      <ContentLoader width={400} height={80} viewBox="0 0 400 80">
        <circle cx={22} cy={32} r="22px" />
        <rect x={60} y={30} rx="3" ry="3" width="200" height="10" />
        <rect x={60} y={10} ry="3" width="100" height="10" />
      </ContentLoader>
    </Box>
  );
};

Row.propTypes = {
  ariaAttributes: object.isRequired,
  index: number.isRequired,
  style: object.isRequired,
  employeesRef: object.isRequired,
};

const EmployeesList = ({ employeesRef, loadMoreItems, totalCount, isFetching }) => {
  const rowProps = useMemo(() => ({ employeesRef }), [employeesRef]);
  const isRowLoaded = useCallback(index => !!employeesRef.current[index], [employeesRef]);
  const loadMoreRows = useCallback(
    (startIndex, stopIndex) => Promise.resolve(loadMoreItems(startIndex, stopIndex)),
    [loadMoreItems],
  );

  return (
    <Box
      sx={[
        {
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        },
        isFetching
          ? {
              opacity: 0.5,
            }
          : {
              opacity: 1,
            },
        isFetching
          ? {
              pointerEvents: 'none',
            }
          : {
              pointerEvents: 'initial',
            },
      ]}
    >
      <InfiniteLoader
        minimumBatchSize={100}
        isRowLoaded={isRowLoaded}
        rowCount={totalCount}
        loadMoreRows={loadMoreRows}
      >
        {({ onRowsRendered }) => (
          <List
            onRowsRendered={(visible, all) => onRowsRendered(all)}
            rowComponent={Row}
            rowCount={totalCount}
            rowHeight={ROW_HEIGHT}
            rowProps={rowProps}
            style={{ flex: '1 1 auto', height: '100%', width: '100%', minHeight: 0 }}
          />
        )}
      </InfiniteLoader>
    </Box>
  );
};

EmployeesList.propTypes = {
  employeesRef: object.isRequired,
  loadMoreItems: func.isRequired,
  totalCount: number.isRequired,
  isFetching: bool.isRequired,
};

export default EmployeesList;
